import { zodResolver } from "@hookform/resolvers/zod";
import { getLocalTimeZone, now } from "@internationalized/date";
import { useMutation } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { mutationFnPOST, mutationFnPUT } from "api/tanStackQuery/helpers";
import { ChecklistFormContent } from "features/CenterContent/RoleContent/VehicleChecklist/ChecklistFormContent";
import {
  Employee,
  useEmployeeSearch,
} from "features/CenterContent/RoleContent/VehicleChecklist/hooks/useEmployeeSearch";
import {
  ChecklistFormData,
  checklistFormSchema,
} from "features/CenterContent/RoleContent/VehicleChecklist/schema/formSchema";
import { FilloutRequest } from "features/CenterContent/RoleContent/VehicleChecklist/schema/requestSchema";
import { FillOut } from "features/CenterContent/RoleContent/VehicleChecklist/schema/responseSchema";
import { FilloutFormToRequest } from "features/CenterContent/RoleContent/VehicleChecklist/schema/transformers";
import { VehicleSetVariant } from "features/CenterContent/RoleContent/VehicleChecklist/VehicleSetVariant";
import { FC, useCallback, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { ActionModal } from "shared/components/ActionModal";
import { dateTimeSchemaResponse } from "shared/form/dateTimeSchema";

interface NewChecklistModalProps {
  currentFillOut: FillOut | null;
  vehicleSetVariants: VehicleSetVariant[];
  employees: Employee[];
  onClose: () => void;
  refetch: () => void;
  potentialSetIdFromSearchString?: string;
}

function makeDefaultFormValues(
  currentFillOut: NewChecklistModalProps["currentFillOut"],
) {
  if (currentFillOut) {
    return {
      dateTime: dateTimeSchemaResponse.parse(currentFillOut.executedAt),
      comment: "",
      executor: currentFillOut.executor,
      fillOutType: currentFillOut.checklistType,
      rollingStockId: currentFillOut.rollingStock,
      vehicleType: currentFillOut.vehicleType,
      trainOwner: currentFillOut.companyId,
    };
  }

  return {
    dateTime: now(getLocalTimeZone()),
    comment: "",
    executor: undefined,
    fillOutType: undefined,
    rollingStockId: undefined,
    vehicleType: undefined,
    trainOwner: undefined,
  };
}

const ChecklistModal: FC<NewChecklistModalProps> = ({
  currentFillOut,
  vehicleSetVariants,
  employees,
  onClose,
  refetch,
  potentialSetIdFromSearchString,
}) => {
  const formMethods = useForm<ChecklistFormData>({
    resolver: zodResolver(checklistFormSchema),
    defaultValues: makeDefaultFormValues(currentFillOut),
  });
  const { control, setValue, handleSubmit } = formMethods;

  const watchedValues = useWatch({ control });

  const [inputValue, setInputValue] = useState<string>("");

  // Add employee search based on the combobox input
  const { data: searchedEmployees } = useEmployeeSearch(inputValue);

  // Use searched employees if available, otherwise fall back to the prop
  const employeesToUse =
    searchedEmployees && searchedEmployees.length > 0
      ? searchedEmployees
      : employees;

  const { mutate: putChecklist, status: putStatus } = useMutation({
    mutationFn: (data: FilloutRequest) => {
      if (!currentFillOut) {
        throw new Error("currentFillOut is null");
      }
      return mutationFnPUT<undefined, any>(
        `${getBackendUrl()}/checklist/fill-out/${currentFillOut.fillOutId}`,
        data,
      );
    },
  });

  const { mutate: postChecklist, status: postStatus } = useMutation({
    mutationFn: (data: FilloutRequest) =>
      mutationFnPOST<undefined, any>(
        `${getBackendUrl()}/checklist/fill-out`,
        data,
      ),
  });

  const isUnchanged = useMemo(
    () =>
      !!currentFillOut &&
      JSON.stringify(makeDefaultFormValues(currentFillOut)) ===
        JSON.stringify(watchedValues),
    [currentFillOut, watchedValues],
  );

  const onSubmit = useCallback(
    (data: ChecklistFormData) => {
      const checklistData: FilloutRequest = FilloutFormToRequest(data);

      const onSuccess = () => {
        refetch();
        onClose();
      };

      if (currentFillOut) {
        putChecklist(checklistData, { onSuccess });
      } else {
        postChecklist(checklistData, { onSuccess });
      }
    },
    [currentFillOut, putChecklist, postChecklist],
  );

  return (
    <FormProvider {...formMethods}>
      <ActionModal
        onClose={onClose}
        onSubmit={handleSubmit(onSubmit)}
        title={currentFillOut ? "Endre Kvittering" : "Opprett kvittering"}
        actionTitle={currentFillOut ? "Lagre endringer" : "Opprett kvittering"}
        loading={postStatus === "pending" || putStatus === "pending"}
        isSuccess={postStatus === "success" || putStatus === "success"}
        isError={postStatus === "error" || putStatus === "error"}
        successMessage="Kvittering lagret"
        failureMessage="Kunne ikke lagre kvittering"
        isSubmitDisabled={isUnchanged}
      >
        <ChecklistFormContent
          currentFillOut={currentFillOut}
          vehicleSetVariants={vehicleSetVariants}
          onVehicleSelect={(value, setType) => {
            setValue("rollingStockId", value);
            setValue("vehicleType", setType);
          }}
          formMethods={formMethods}
          employees={employeesToUse ?? []}
          input={inputValue}
          setInput={setInputValue}
          potentialSetIdFromSearchString={potentialSetIdFromSearchString}
        />
      </ActionModal>
    </FormProvider>
  );
};

export default ChecklistModal;
